import { action, observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Models from 'Models/Entities';
import * as Enums from '../Enums';
/* eslint-disable max-len */

export interface IManagerBookingDiscountEntityAttributes extends IModelAttributes {
	discountAmount: number;
	discountType: Enums.discountType;

	managerBookingDiscountUsages: Array<
		Models.ManagerBookingDiscountUsagesEntity | Models.IManagerBookingDiscountUsagesEntityAttributes
	>;
	alterationId: string;
	alteration: Models.AlterationEntity | Models.IAlterationEntityAttributes;
}
@entity('ManagerBookingDiscountEntity', 'Manager booking discount')
export default class ManagerBookingDiscountEntity extends Model implements IManagerBookingDiscountEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<ManagerBookingDiscountEntity, number>()
	public discountAmount: number;

	/**
	 * The type of entity that the discount applies to.
	 */
	@observable
	@attribute<ManagerBookingDiscountEntity, Enums.discountType>()
	public discountType: Enums.discountType;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public managerBookingDiscountUsages: Models.ManagerBookingDiscountUsagesEntity[] = [];

	@observable
	@attribute()
	public alterationId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public alteration: Models.AlterationEntity;

	constructor(attributes?: Partial<IManagerBookingDiscountEntityAttributes>) {
		super(attributes);

		this.managerBookingDiscountUsages = [];

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<IManagerBookingDiscountEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.discountAmount !== undefined) {
				this.discountAmount = attributes.discountAmount;
			}
			if (attributes.discountType !== undefined) {
				this.discountType = attributes.discountType;
			}
			if (
				attributes.managerBookingDiscountUsages !== undefined &&
				Array.isArray(attributes.managerBookingDiscountUsages)
			) {
				if (!this.managerBookingDiscountUsages) {
					this.managerBookingDiscountUsages = [];
				}
				for (const model of attributes.managerBookingDiscountUsages) {
					if (model instanceof Models.ManagerBookingDiscountUsagesEntity) {
						this.managerBookingDiscountUsages.push(model);
					} else {
						this.managerBookingDiscountUsages.push(new Models.ManagerBookingDiscountUsagesEntity(model));
					}
				}
			}
			if (attributes.alterationId !== undefined) {
				this.alterationId = attributes.alterationId;
			}
			if (attributes.alteration !== undefined) {
				if (attributes.alteration === null) {
					this.alteration = attributes.alteration;
				} else if (attributes.alteration instanceof Models.AlterationEntity) {
					this.alteration = attributes.alteration;
					this.alterationId = attributes.alteration.id;
				} else {
					this.alteration = new Models.AlterationEntity(attributes.alteration);
					this.alterationId = this.alteration.id;
				}
			}
		}
	}
}
